import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  adminService,
  type SectionEntry,
  type ClassLevelEntry,
  type TeacherAccount,
} from '@/services/admin';
import type { ClassSummary, StudentRecord } from '@/types';

const NEW_SECTION_VALUE = '__new_section__';

const selectClassName =
  'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30';

const resolveErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string; error?: string } } }).response;
    return response?.data?.message ?? response?.data?.error ?? fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
};

const teacherFullName = (teacher: TeacherAccount) =>
  `${teacher.firstName} ${teacher.lastName}`.trim();

const teacherLabelForGroup = (group: ClassSummary, teachers: TeacherAccount[]) => {
  const fromGroup = group.teacherName?.trim();
  if (fromGroup) return fromGroup;
  if (group.teacherId) {
    const teacher = teachers.find((item) => item.id === group.teacherId);
    if (teacher) return teacherFullName(teacher);
  }
  return 'Unassigned';
};

const formatRowTeacherText = (groups: ClassSummary[], teachers: TeacherAccount[]) => {
  if (!groups.length) return 'Unassigned';
  if (groups.length === 1) return teacherLabelForGroup(groups[0], teachers);
  return groups
    .map((group) => `${group.sectionName || '—'}: ${teacherLabelForGroup(group, teachers)}`)
    .join(' · ');
};

const teacherOptionLabel = (
  teacher: TeacherAccount,
  classes: ClassSummary[],
  currentClassId: string,
) => {
  const name = teacherFullName(teacher);
  const otherGroups = classes.filter(
    (classItem) => classItem.teacherId === teacher.id && classItem.id !== currentClassId,
  );
  if (!otherGroups.length) return name;
  return `${name} (${otherGroups.map((classItem) => classItem.name).join(', ')})`;
};

type AllotSectionControlProps = {
  unused: SectionEntry[];
  disabled?: boolean;
  onAllot: (sectionId: string) => void;
  onCreateName: (name: string) => void;
};

function AllotSectionControl({ unused, disabled, onAllot, onCreateName }: AllotSectionControlProps) {
  const [mode, setMode] = useState<'select' | 'new'>('select');
  const [newName, setNewName] = useState('');
  const [selectValue, setSelectValue] = useState('');

  const reset = () => {
    setMode('select');
    setNewName('');
    setSelectValue('');
  };

  if (mode === 'new') {
    return (
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = newName.trim();
          if (!trimmed) return;
          onCreateName(trimmed);
          reset();
        }}
      >
        <input
          type="text"
          className={`w-36 ${selectClassName}`}
          placeholder="e.g., B"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          disabled={disabled}
          autoFocus
        />
        <Button type="submit" variant="secondary" disabled={disabled || !newName.trim()}>
          Add
        </Button>
        <button
          type="button"
          className="text-sm text-slate-500 hover:text-slate-700"
          onClick={reset}
          disabled={disabled}
        >
          Cancel
        </button>
      </form>
    );
  }

  return (
    <select
      className={selectClassName}
      aria-label="Allot section"
      value={selectValue}
      disabled={disabled}
      onChange={(event) => {
        const value = event.target.value;
        if (value === NEW_SECTION_VALUE) {
          setMode('new');
          setSelectValue('');
          return;
        }
        if (value) onAllot(value);
        setSelectValue('');
      }}
    >
      <option value="">Allot section…</option>
      {unused.map((section) => (
        <option key={section.id} value={section.id}>
          {section.name}
        </option>
      ))}
      <option value={NEW_SECTION_VALUE}>+ New section name</option>
    </select>
  );
}

type PendingDelete =
  | { kind: 'class'; level: ClassLevelEntry; studentCount: number }
  | { kind: 'section'; group: ClassSummary; studentCount: number };

type TypeDeleteDialogProps = {
  isOpen: boolean;
  title: string;
  description: string;
  pending?: boolean;
  confirmValue: string;
  onConfirmValueChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
};

function TypeDeleteDialog({
  isOpen,
  title,
  description,
  pending,
  confirmValue,
  onConfirmValueChange,
  onCancel,
  onConfirm,
}: TypeDeleteDialogProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-600"
            disabled={pending || confirmValue !== 'DELETE'}
            onClick={onConfirm}
          >
            {pending ? 'Deleting...' : 'Delete'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-slate-600">{description}</p>
      <label className="mb-1 mt-4 block text-sm font-medium text-slate-700">Type DELETE to confirm</label>
      <input
        type="text"
        className={`w-full ${selectClassName}`}
        value={confirmValue}
        onChange={(event) => onConfirmValueChange(event.target.value)}
        autoFocus
      />
    </Modal>
  );
}

const studentCountForGroups = (groups: ClassSummary[]) =>
  groups.reduce((sum, group) => sum + (typeof group.studentCount === 'number' ? group.studentCount : 0), 0);

const deleteClassDescription = (className: string, studentCount: number) =>
  studentCount > 0
    ? `${studentCount} student${studentCount === 1 ? '' : 's'} in ${className} will move to Unassigned. They stay in the school. Assign them again in Records.`
    : `${className} has nobody to move. The class definition will be removed.`;

const deleteSectionDescription = (sectionName: string, studentCount: number) =>
  `${studentCount} student${studentCount === 1 ? '' : 's'} in ${sectionName} will move to Unassigned. They stay in the school. Assign them again in Records.`;

export default function AdminSchoolStructurePage() {
  const navigate = useNavigate();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [editingLevel, setEditingLevel] = useState<ClassLevelEntry | null>(null);
  const [editName, setEditName] = useState('');
  const [savingClassId, setSavingClassId] = useState<string | null>(null);
  const [deletingLevelId, setDeletingLevelId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [deleteTyped, setDeleteTyped] = useState('');
  const queryClient = useQueryClient();

  const invalidateStructure = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-class-levels'] });
    queryClient.invalidateQueries({ queryKey: ['admin-sections'] });
    queryClient.invalidateQueries({ queryKey: ['admin-classes'] });
    queryClient.invalidateQueries({ queryKey: ['admin-teachers'] });
    queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
    queryClient.invalidateQueries({ queryKey: ['admin-students'] });
  };

  const { data: classLevels = [], isLoading: loadingLevels } = useQuery<ClassLevelEntry[]>({
    queryKey: ['admin-class-levels'],
    queryFn: adminService.listClassLevels,
  });

  const { data: sections = [], isLoading: loadingSections } = useQuery<SectionEntry[]>({
    queryKey: ['admin-sections'],
    queryFn: adminService.listSections,
  });

  const { data: classes = [], isLoading: loadingClasses } = useQuery<ClassSummary[]>({
    queryKey: ['admin-classes'],
    queryFn: adminService.listClasses,
  });

  const { data: teachers = [], isLoading: loadingTeachers } = useQuery<TeacherAccount[]>({
    queryKey: ['admin-teachers'],
    queryFn: adminService.listTeachers,
  });

  const { data: students = [] } = useQuery<StudentRecord[]>({
    queryKey: ['admin-students'],
    queryFn: adminService.listStudents,
  });

  const classList = Array.isArray(classes) ? classes : [];
  const sectionList = Array.isArray(sections) ? sections : [];
  const studentList = Array.isArray(students) ? students : [];
  const unassignedCount = studentList.filter((student) => !student.classId).length;
  const levelList = useMemo(
    () => [...classLevels].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [classLevels],
  );

  const groupsByGrade = useMemo(() => {
    const grouped = new Map<string, ClassSummary[]>();
    for (const classItem of classList) {
      const key = classItem.gradeLevel || '';
      const bucket = grouped.get(key) ?? [];
      bucket.push(classItem);
      grouped.set(key, bucket);
    }
    for (const bucket of grouped.values()) {
      bucket.sort((a, b) => (a.sectionName || '').localeCompare(b.sectionName || '') || a.name.localeCompare(b.name));
    }
    return grouped;
  }, [classList]);

  const knownGradeNames = useMemo(() => new Set(levelList.map((level) => level.name)), [levelList]);
  const orphanGradeNames = useMemo(() => {
    const names = new Set<string>();
    for (const classItem of classList) {
      if (classItem.gradeLevel && !knownGradeNames.has(classItem.gradeLevel)) {
        names.add(classItem.gradeLevel);
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [classList, knownGradeNames]);

  const unusedSectionsForGrade = (gradeLevel: string) => {
    const used = new Set(
      classList
        .filter((classItem) => classItem.gradeLevel === gradeLevel && classItem.sectionId)
        .map((classItem) => classItem.sectionId as string),
    );
    return sectionList.filter((section) => !used.has(section.id));
  };

  const nextSortOrder = levelList.reduce((max, level) => Math.max(max, level.sortOrder), 0) + 1;

  const createLevelMutation = useMutation({
    mutationFn: adminService.createClassLevel,
    onSuccess: () => {
      invalidateStructure();
      setIsAddOpen(false);
      setAddName('');
    },
    onError: (error) => alert(resolveErrorMessage(error, 'Unable to create class definition.')),
  });

  const updateLevelMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string } }) =>
      adminService.updateClassLevel(id, data),
    onSuccess: (_data, variables) => {
      invalidateStructure();
      setEditingLevel((prev) =>
        prev && prev.id === variables.id ? { ...prev, name: variables.data.name } : prev,
      );
    },
    onError: (error) => alert(resolveErrorMessage(error, 'Unable to update class definition.')),
  });

  const createClassMutation = useMutation({
    mutationFn: adminService.createClass,
    onSuccess: invalidateStructure,
    onError: (error) => alert(resolveErrorMessage(error, 'Unable to allot section.')),
  });

  const updateClassMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { teacherId: string | null } }) =>
      adminService.updateClass(id, data),
    onSuccess: () => {
      setSavingClassId(null);
      invalidateStructure();
    },
    onError: (error) => {
      setSavingClassId(null);
      alert(resolveErrorMessage(error, 'Unable to update homeroom teacher.'));
    },
  });

  const deleteClassMutation = useMutation({
    mutationFn: adminService.deleteClass,
    onSuccess: invalidateStructure,
    onError: (error) => alert(resolveErrorMessage(error, 'Unable to remove class group.')),
  });

  const closeAddModal = () => {
    setIsAddOpen(false);
    setAddName('');
  };

  const closeEditModal = () => {
    setEditingLevel(null);
    setEditName('');
  };

  const openEditModal = (level: ClassLevelEntry) => {
    setEditingLevel(level);
    setEditName(level.name);
  };

  const handleAddClass = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = addName.trim();
    if (!trimmed) return;
    createLevelMutation.mutate({ name: trimmed, sortOrder: nextSortOrder });
  };

  const handleRenameClass = (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingLevel) return;
    const trimmed = editName.trim();
    if (!trimmed || trimmed === editingLevel.name) return;
    updateLevelMutation.mutate({ id: editingLevel.id, data: { name: trimmed } });
  };

  const handleAllotSection = (gradeLevel: string, sectionId: string) => {
    if (!sectionId) return;
    createClassMutation.mutate({ gradeLevel, sectionId });
  };

  const handleCreateAndAllot = async (gradeLevel: string, sectionName: string) => {
    const trimmed = sectionName.trim();
    if (!trimmed) return;
    const existing = sectionList.find(
      (section) => section.name.localeCompare(trimmed, undefined, { sensitivity: 'accent' }) === 0,
    );
    if (existing) {
      handleAllotSection(gradeLevel, existing.id);
      return;
    }
    try {
      const created = await adminService.createSection({ name: trimmed });
      createClassMutation.mutate({ gradeLevel, sectionId: created.id });
    } catch (error) {
      alert(resolveErrorMessage(error, 'Unable to create section.'));
    }
  };

  const handleTeacherChange = (classId: string, teacherId: string) => {
    setSavingClassId(classId);
    updateClassMutation.mutate({ id: classId, data: { teacherId: teacherId || null } });
  };

  const handleRemoveGroup = (classItem: ClassSummary) => {
    if (typeof classItem.studentCount !== 'number') return;
    if (classItem.studentCount > 0) {
      setDeleteTyped('');
      setPendingDelete({
        kind: 'section',
        group: classItem,
        studentCount: classItem.studentCount,
      });
      return;
    }
    if (confirm(`Remove section "${classItem.sectionName || classItem.name}"? It has no students.`)) {
      deleteClassMutation.mutate(classItem.id);
    }
  };

  const openDeleteClass = (level: ClassLevelEntry) => {
    const groups = groupsByGrade.get(level.name) ?? [];
    setDeleteTyped('');
    setPendingDelete({
      kind: 'class',
      level,
      studentCount: studentCountForGroups(groups),
    });
  };

  const closePendingDelete = () => {
    if (deletingLevelId) return;
    setPendingDelete(null);
    setDeleteTyped('');
  };

  const executePendingDelete = async () => {
    if (!pendingDelete || deleteTyped !== 'DELETE') return;
    const current = pendingDelete;
    setDeletingLevelId(current.kind === 'class' ? current.level.id : current.group.id);
    try {
      if (current.kind === 'section') {
        await adminService.deleteClass(current.group.id);
      } else {
        const freshClasses = await adminService.listClasses();
        const freshList = Array.isArray(freshClasses) ? freshClasses : [];
        const freshGroups = freshList.filter((classItem) => classItem.gradeLevel === current.level.name);
        for (const group of freshGroups) {
          await adminService.deleteClass(group.id);
        }
        await adminService.deleteClassLevel(current.level.id);
        if (editingLevel?.id === current.level.id) {
          closeEditModal();
        }
      }
      setPendingDelete(null);
      setDeleteTyped('');
      invalidateStructure();
    } catch (error) {
      alert(resolveErrorMessage(error, 'Unable to delete.'));
      invalidateStructure();
    } finally {
      setDeletingLevelId(null);
    }
  };

  const allotPending = createClassMutation.isPending;
  const mapLoading = loadingLevels || loadingSections || loadingClasses || loadingTeachers;
  const editingGroups = editingLevel ? (groupsByGrade.get(editingLevel.name) ?? []) : [];

  const renderAllot = (gradeLevel: string) => (
    <AllotSectionControl
      unused={unusedSectionsForGrade(gradeLevel)}
      disabled={allotPending}
      onAllot={(sectionId) => handleAllotSection(gradeLevel, sectionId)}
      onCreateName={(name) => {
        void handleCreateAndAllot(gradeLevel, name);
      }}
    />
  );

  const renderRow = (key: string, className: string, groups: ClassSummary[], level?: ClassLevelEntry) => {
    const count = studentCountForGroups(groups);
    return (
    <tr key={key} className="bg-white">
      <td className="px-4 py-3 text-sm font-medium text-slate-900">{className}</td>
      <td className="px-4 py-3 text-sm text-slate-600">{formatRowTeacherText(groups, teachers)}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {groups.map((group) =>
            group.sectionName ? (
              <span
                key={group.id}
                className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
              >
                {group.sectionName}
              </span>
            ) : (
              <span key={group.id} className="text-xs text-slate-400">
                No section
              </span>
            ),
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-slate-700">
        <button
          type="button"
          className="text-blue-600 hover:text-blue-800"
          onClick={() => navigate(`/admin/students?grade=${encodeURIComponent(className)}`)}
        >
          {count}
        </button>
      </td>
      <td className="px-4 py-3 text-right text-sm">
        {level ? (
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => openEditModal(level)}
              className="text-blue-600 hover:text-blue-800"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => openDeleteClass(level)}
              className="text-rose-600 hover:text-rose-800"
              disabled={deletingLevelId === level.id}
            >
              {deletingLevelId === level.id ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        ) : (
          <span className="text-xs text-slate-400">No class definition</span>
        )}
      </td>
    </tr>
    );
  };

  return (
    <DashboardShell title="School Structure" role="admin">
      <p className="mb-4 text-sm text-slate-500">
        One row per class. Student counts open Records. Use Edit to allot sections and set homeroom teachers.
        Delete moves students to Unassigned instead of removing them.
      </p>

      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-800">Classes</h3>
        <Button variant="secondary" onClick={() => setIsAddOpen(true)}>
          Add class
        </Button>
      </div>

      {mapLoading && <p className="mb-3 text-sm text-slate-500">Loading class register…</p>}

      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Class
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Teacher
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Sections
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Students
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {' '}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {levelList.map((level) =>
                renderRow(level.id, level.name, groupsByGrade.get(level.name) ?? [], level),
              )}
              {orphanGradeNames.map((gradeName) =>
                renderRow(`orphan-${gradeName}`, gradeName, groupsByGrade.get(gradeName) ?? []),
              )}
              {unassignedCount > 0 && (
                <tr className="bg-amber-50/60">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">Unassigned</td>
                  <td className="px-4 py-3 text-sm text-slate-400">—</td>
                  <td className="px-4 py-3 text-sm text-slate-400">—</td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => navigate('/admin/students?class=unassigned')}
                    >
                      {unassignedCount}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right text-sm">
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => navigate('/admin/students?class=unassigned')}
                    >
                      Open in Records
                    </button>
                  </td>
                </tr>
              )}
              {!loadingLevels && !levelList.length && !orphanGradeNames.length && unassignedCount === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                    No classes yet. Use Add class to create the first row.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        isOpen={isAddOpen}
        onClose={closeAddModal}
        title="Add class"
        footer={
          <>
            <Button variant="secondary" onClick={closeAddModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddClass} disabled={createLevelMutation.isPending}>
              {createLevelMutation.isPending ? 'Saving...' : 'Create'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleAddClass} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Class name</label>
            <input
              type="text"
              required
              placeholder="e.g., Grade 1"
              className={`w-full ${selectClassName}`}
              value={addName}
              onChange={(event) => setAddName(event.target.value)}
              autoFocus
            />
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(editingLevel)}
        onClose={closeEditModal}
        title={editingLevel ? `Edit ${editingLevel.name}` : 'Edit class'}
        footer={
          <>
            {editingLevel && (
              <button
                type="button"
                onClick={() => {
                  if (editingLevel) openDeleteClass(editingLevel);
                }}
                className="mr-auto text-sm text-rose-600 hover:text-rose-800 disabled:opacity-50"
                disabled={deletingLevelId === editingLevel.id}
              >
                {deletingLevelId === editingLevel.id ? 'Deleting...' : 'Delete class'}
              </button>
            )}
            <Button variant="secondary" onClick={closeEditModal}>
              Done
            </Button>
          </>
        }
      >
        {editingLevel && (
          <div className="space-y-6">
            <form onSubmit={handleRenameClass} className="space-y-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">Class name</label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  required
                  className={`min-w-[12rem] flex-1 ${selectClassName}`}
                  value={editName}
                  onChange={(event) => setEditName(event.target.value)}
                />
                <Button
                  type="submit"
                  variant="secondary"
                  disabled={
                    updateLevelMutation.isPending ||
                    !editName.trim() ||
                    editName.trim() === editingLevel.name
                  }
                >
                  {updateLevelMutation.isPending ? 'Saving...' : 'Save name'}
                </Button>
              </div>
            </form>

            <div>
              <h4 className="mb-2 text-sm font-semibold text-slate-800">Sections</h4>
              <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
                {editingGroups.map((group) => (
                  <li key={group.id} className="flex flex-wrap items-center gap-3 px-3 py-3">
                    {group.sectionName ? (
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                        {group.sectionName}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">No section</span>
                    )}
                    <select
                      className={`min-w-[12rem] flex-1 ${selectClassName}`}
                      value={group.teacherId || ''}
                      disabled={savingClassId === group.id}
                      onChange={(event) => handleTeacherChange(group.id, event.target.value)}
                      aria-label={`Homeroom teacher for ${group.sectionName || group.name}`}
                    >
                      <option value="">Unassigned</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacherOptionLabel(teacher, classList, group.id)}
                        </option>
                      ))}
                    </select>
                    <span className="text-xs text-slate-500">
                      {group.studentCount} student{group.studentCount === 1 ? '' : 's'}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveGroup(group)}
                      className="text-sm text-rose-600 hover:text-rose-800"
                      disabled={deleteClassMutation.isPending || Boolean(deletingLevelId)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
                {!editingGroups.length && (
                  <li className="px-3 py-4 text-center text-sm text-slate-500">
                    No sections allotted yet.
                  </li>
                )}
              </ul>
              <div className="mt-3">{renderAllot(editingLevel.name)}</div>
            </div>
          </div>
        )}
      </Modal>
      <TypeDeleteDialog
        isOpen={Boolean(pendingDelete)}
        title={
          pendingDelete?.kind === 'section'
            ? `Remove ${pendingDelete.group.sectionName || pendingDelete.group.name}`
            : pendingDelete
              ? `Delete ${pendingDelete.level.name}`
              : 'Delete'
        }
        description={
          pendingDelete?.kind === 'section'
            ? deleteSectionDescription(
                pendingDelete.group.sectionName || pendingDelete.group.name,
                pendingDelete.studentCount,
              )
            : pendingDelete
              ? deleteClassDescription(pendingDelete.level.name, pendingDelete.studentCount)
              : ''
        }
        pending={Boolean(deletingLevelId)}
        confirmValue={deleteTyped}
        onConfirmValueChange={setDeleteTyped}
        onCancel={closePendingDelete}
        onConfirm={() => {
          void executePendingDelete();
        }}
      />
    </DashboardShell>
  );
}
